/**
 * @fileoverview Hook for managing URL search params state
 *
 * This module provides utilities for syncing component state with URL search parameters.
 * It uses the browser History API directly (instead of Next.js router) to avoid triggering
 * navigation cycles that cause Suspense re-suspension and focus loss.
 *
 * Uses `qs` library with dot notation for clean nested object serialization
 * (e.g., `?pricing.section=tts` instead of `?pricing%5Bsection%5D=tts`).
 *
 * ## Important Notes
 *
 * ⚠️ **Dot Notation:** Avoid using dots in parameter keys (e.g., `"my.key"`) as they will be
 * interpreted as nested objects. Use underscores or camelCase instead (`"my_key"` or `"myKey"`).
 *
 * @example
 * ```tsx
 * export function MyComponent() {
 *   const { getSearchParam, updateSearchParams } = useSearchParamsState();
 *   return <div>...</div>;
 * }
 * ```
 *
 * For a complete example of implementation, see:
 * @see PricingTableContext in `frontend-next/src/app/_ui/components/pricing/offers/PricingTableContext.tsx`
 */

"use client";

import { useCallback } from "react";
import qs from "qs";

/**
 * Type for the updateSearchParams function returned by useSearchParamsState
 * Accepts strings or objects (which will be automatically flattened using qs)
 */
export type UpdateSearchParams = (
  updates: Record<string, string | Record<string, unknown> | null | undefined>,
  options?: {
    hash?: string;
    preserveHash?: boolean;
  },
) => void;

/**
 * Hook that provides search params and a function to update them.
 * Uses the browser History API directly for synchronous, focus-preserving URL updates.
 *
 * **Automatic Nested Object Handling:**
 * - When updating params with object values, they are automatically flattened using qs
 * - When reading params, use `getSearchParam()` to automatically reconstruct nested objects
 *
 * ⚠️ **Dot Notation Warning:** This hook uses dot notation for nested objects (e.g., `pricing.section=tts`).
 * Avoid using dots in parameter keys (e.g., `"my.key"`) as they will be interpreted as nested objects.
 * Use underscores or camelCase instead (e.g., `"my_key"` or `"myKey"`).
 *
 * @example Basic usage with strings
 * ```tsx
 * const { updateSearchParams } = useSearchParamsState();
 *
 * // Update with string
 * updateSearchParams({ tab: 'settings' }, { hash: 'section' });
 * ```
 *
 * @example Automatic nested object flattening
 * ```tsx
 * const { getSearchParam, updateSearchParams } = useSearchParamsState();
 *
 * // Update with object (automatically flattened using qs)
 * updateSearchParams({
 *   pricingState: { section: 'tts', billing: 'annual' }
 * }, { hash: 'pricing-table' });
 * // Results in URL: ?pricingState.section=tts&pricingState.billing=annual#pricing-table
 *
 * // Read and reconstruct nested object automatically
 * const state = getSearchParam<MyStateType>('pricingState');
 * ```
 *
 * @returns Object containing getSearchParam and updateSearchParams functions
 *
 * @see PricingTableContext in `frontend-next/src/app/_ui/components/pricing/offers/PricingTableContext.tsx`
 */
export function useSearchParamsState() {
  /**
   * Update the search params using the browser History API directly.
   * This avoids triggering Next.js navigation cycles that cause Suspense
   * re-suspension, focus loss, and delayed URL updates.
   *
   * @param updates - The updates to apply to the search params
   * @param options - The options for the update
   * @returns void
   * @see buildUrlWithSearchParams for the implementation of the update
   */
  const updateSearchParams: UpdateSearchParams = useCallback(
    (
      updates: Record<string, string | Record<string, unknown> | null | undefined>,
      options?: {
        hash?: string;
        preserveHash?: boolean;
      },
    ) => {
      if (typeof window === "undefined") {
        return;
      }

      const { url, hash } = buildUrlWithSearchParams(updates, {
        hash: options?.hash,
        preserveHash: options?.preserveHash ?? true,
      });

      // Use history.replaceState directly to update the URL synchronously
      // without triggering Next.js navigation or Suspense re-suspension.
      const fullUrl = url + (hash ? "#" + hash : "") || window.location.pathname;
      window.history.replaceState(window.history.state, "", fullUrl);
    },
    [],
  );

  /**
   * Get a param value, automatically reconstructing nested objects using qs.
   * Reads directly from window.location.search for always-current values.
   * @param key - The parameter key to read
   * @returns The parsed value (object if nested structure exists, string otherwise) or null
   */
  const getSearchParam = useCallback(<T = unknown>(key: string): T | string | null => {
    if (typeof window === "undefined") return null;

    const parsed = qs.parse(window.location.search, { ignoreQueryPrefix: true, allowDots: true });
    const value = parsed[key];

    if (value === undefined) return null;
    return value as T | string;
  }, []);

  return { getSearchParam, updateSearchParams };
}

/**
 * Build URL with updated search params
 * Can preserve the current hash or set a specific hash
 * Automatically flattens nested objects using qs
 * Reads current params from window.location.search
 *
 * @internal This is a helper function used by useSearchParamsState
 * @returns Object with url (query string) and hash (without #) separately
 */
function buildUrlWithSearchParams(
  updates: Record<string, string | Record<string, unknown> | null | undefined>,
  options?: {
    preserveHash?: boolean;
    hash?: string;
  },
): { url: string; hash: string | null } {
  // Read current params from window and parse using qs
  const currentSearch = typeof window !== "undefined" ? window.location.search : "";
  const currentParams = qs.parse(currentSearch, { ignoreQueryPrefix: true, allowDots: true });

  // Merge updates into current params
  const mergedParams: Record<string, any> = { ...currentParams };
  Object.entries(updates).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") {
      delete mergedParams[key];
    } else {
      mergedParams[key] = value;
    }
  });

  // Stringify using qs (automatically flattens nested objects)
  // Use allowDots for cleaner URLs (e.g., pricing.section instead of pricing[section])
  const queryString = qs.stringify(mergedParams, { encode: true, allowDots: true });
  const url = queryString ? `?${queryString}` : "";

  let hash: string | null = null;

  // Determine which hash to use
  if (options?.hash) {
    // Remove leading # if present
    hash = options.hash.startsWith("#") ? options.hash.slice(1) : options.hash;
  }
  // Otherwise preserve hash if option is enabled and hash exists
  else if (options?.preserveHash && typeof window !== "undefined" && window.location.hash) {
    // Remove leading # from existing hash
    hash = window.location.hash.slice(1);
  }

  return { url, hash };
}
